import {
	Component,
	EventEmitter,
	Input,
	inject,
	NgZone,
	OnChanges,
	OnDestroy,
	OnInit,
	Output,
	SimpleChanges,
} from '@angular/core';
import { BrandingService } from '@services/branding/branding.service';
import { FeatureFlagService } from '@services/branding/feature-flag.service';
import { CreateCryptoOrderResponse, PaymentsService } from '@services/payments/payments.service';
import { BrandIconComponent } from '@shared/components/brand-icon/brand-icon.component';
import type { BrandIconName } from '@shared/components/brand-icon/brand-icons';
import { ButtonComponent } from '@shared/components/button/button.component';
import { SpinnerComponent } from '@shared/components/spinner/spinner.component';
import { SharedModule } from '@shared/modules/shared.module';
import QRCode from 'qrcode';
import { Subscription } from 'rxjs';

/** Paid by bank transfer the user confirms later (pending order) */
export type ManualPaymentMethod = 'revolut' | 'bizum';
/** Paid to one of our wallets (pending order with the wallet instructions) */
export type CryptoPaymentMethod = 'crypto_btc' | 'crypto_usdt_trc20' | 'crypto_usdt_erc20';
export type PaymentMethod = 'card' | ManualPaymentMethod | CryptoPaymentMethod;

export function isCryptoPaymentMethod(method: PaymentMethod | null | undefined): method is CryptoPaymentMethod {
	return !!method?.startsWith('crypto_');
}

export interface PaymentMethodOption {
	id: PaymentMethod;
	/** i18n key */
	name: string;
	/** Lucide icon name */
	icon: string;
	/** Real brand logo (crypto) shown instead of the Lucide `icon` */
	brandIcon?: BrandIconName;
	/** i18n key */
	description: string;
	available: boolean;
}

export interface ManualPaymentInfo {
	revolutId: string;
	revolutDeepLink: string;
	bizumPhone: string;
	amount: number;
}

export interface ManualPaymentSelected {
	method: ManualPaymentMethod;
}

export interface ManualPaymentConfirmed {
	orderId: number;
}

@Component({
	selector: 'app-payment-method-modal',
	imports: [SharedModule, ButtonComponent, SpinnerComponent, BrandIconComponent],
	templateUrl: './payment-method-modal.component.html',
	styleUrl: './payment-method-modal.component.scss',
})
export class PaymentMethodModalComponent implements OnChanges, OnInit, OnDestroy {
	private featureFlagService = inject(FeatureFlagService);
	private brandingService = inject(BrandingService);
	private paymentsService = inject(PaymentsService);
	private ngZone = inject(NgZone);

	@Input() visible = false;
	@Input() totalAmount = 0;
	@Input() originalAmount = 0;
	@Input() discountName = '';
	@Input() productName = '';
	@Input() quantity = 0;
	@Input() orderId: number | null = null; // ID of the created order (for manual payment)
	@Input() isCreatingOrder = false; // Loading state while the order is created
	/** Subscriptions have no crypto flow: hide those methods instead of offering a dead button */
	@Input() allowCrypto = true;

	@Output() visibleChange = new EventEmitter<boolean>();
	@Output() paymentMethodSelected = new EventEmitter<PaymentMethod>();
	@Output() cancelled = new EventEmitter<void>();
	@Output() manualPaymentSelected = new EventEmitter<ManualPaymentSelected>(); // When Revolut/Bizum is picked (create the order)
	@Output() manualPaymentConfirmed = new EventEmitter<ManualPaymentConfirmed>(); // When the user confirms "I have paid"
	@Output() cryptoPaymentSelected = new EventEmitter<{ method: CryptoPaymentMethod }>(); // When crypto is picked

	selectedMethod: PaymentMethod | null = null;
	showManualPaymentInstructions = false;

	// Manual payment details (loaded from ApplicationSettings)
	revolutId = '';
	bizumPhone = '';

	// QR Code (Revolut only)
	revolutQrCode = '';
	phoneCopied = false;

	// Crypto payment state
	showCryptoPaymentView = false;
	cryptoWalletAddress = '';
	cryptoAmount = '';
	cryptoCurrency = '';
	cryptoCurrencyLabel = '';
	cryptoExpiresAt: Date | null = null;
	cryptoQrCode = '';
	cryptoCountdownMinutes = 0;
	cryptoCountdownSeconds = 0;
	cryptoAmountCopied = false;
	cryptoAddressCopied = false;
	cryptoOrderStatus = 'PENDING_PAYMENT';
	cryptoPaymentDetected = false;
	private countdownInterval: ReturnType<typeof setInterval> | null = null;
	private statusPollInterval: ReturnType<typeof setInterval> | null = null;
	private statusPollSub: Subscription | null = null;

	private allPaymentMethods: PaymentMethodOption[] = [
		{
			id: 'card',
			name: 'payment.modal.methods.card.name',
			icon: 'CreditCard',
			description: 'payment.modal.methods.card.description',
			available: true,
		},
		{
			id: 'revolut',
			name: 'payment.modal.methods.revolut.name',
			icon: 'Landmark',
			description: 'payment.modal.methods.revolut.description',
			available: true,
		},
		{
			id: 'bizum',
			name: 'payment.modal.methods.bizum.name',
			icon: 'Smartphone',
			description: 'payment.modal.methods.bizum.description',
			available: true,
		},
		{
			id: 'crypto_btc',
			name: 'payment.modal.methods.crypto_btc.name',
			icon: 'Bitcoin',
			brandIcon: 'bitcoin',
			description: 'payment.modal.methods.crypto_btc.description',
			available: true,
		},
		{
			id: 'crypto_usdt_trc20',
			name: 'payment.modal.methods.crypto_usdt_trc20.name',
			icon: 'Coins',
			brandIcon: 'tether',
			description: 'payment.modal.methods.crypto_usdt_trc20.description',
			available: true,
		},
		{
			id: 'crypto_usdt_erc20',
			name: 'payment.modal.methods.crypto_usdt_erc20.name',
			icon: 'Coins',
			brandIcon: 'ethereum',
			description: 'payment.modal.methods.crypto_usdt_erc20.description',
			available: true,
		},
	];

	/** Logo of the selected crypto method (header of the wallet instructions) */
	get selectedCryptoBrand(): BrandIconName | null {
		return this.allPaymentMethods.find((m) => m.id === this.selectedMethod)?.brandIcon ?? null;
	}

	get paymentMethods(): PaymentMethodOption[] {
		return this.allPaymentMethods.filter((method) => {
			if (!this.allowCrypto && method.id.startsWith('crypto_')) return false;
			switch (method.id) {
				case 'card':
					return this.featureFlagService.isEnabled('paymentCard');
				case 'revolut':
					return this.featureFlagService.isEnabled('paymentRevolut');
				case 'bizum':
					return this.featureFlagService.isEnabled('paymentBizum');
				case 'crypto_btc':
					return this.featureFlagService.isEnabled('paymentCryptoBtc');
				case 'crypto_usdt_trc20':
					return this.featureFlagService.isEnabled('paymentCryptoUsdtTrc20');
				case 'crypto_usdt_erc20':
					return this.featureFlagService.isEnabled('paymentCryptoUsdtErc20');
				default:
					return false;
			}
		});
	}

	ngOnInit(): void {
		const branding = this.brandingService.current;
		if (branding) {
			this.revolutId = branding.revolutId || '';
			this.bizumPhone = branding.bizumPhone || '';
			if (this.visible && this.totalAmount > 0) {
				this.generateRevolutQr();
			}
		}
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['visible'] && this.visible && this.totalAmount > 0 && this.revolutId) {
			this.generateRevolutQr();
		}
	}

	private async generateRevolutQr(): Promise<void> {
		if (!this.revolutId) return;
		try {
			const revolutDeepLink = `https://revolut.me/${this.revolutId}?amount=${this.totalAmount.toFixed(2)}&currency=EUR`;
			this.revolutQrCode = await QRCode.toDataURL(revolutDeepLink, {
				width: 200,
				margin: 2,
				color: { dark: '#000000', light: '#ffffff' },
			});
		} catch (err) {
			console.error('Error generating Revolut QR:', err);
		}
	}

	copyBizumPhone(): void {
		if (!this.bizumPhone) return;
		navigator.clipboard.writeText(this.bizumPhone.replace(/\s/g, '')).then(() => {
			this.phoneCopied = true;
			setTimeout(() => {
				this.phoneCopied = false;
			}, 2000);
		});
	}

	selectMethod(method: PaymentMethodOption): void {
		if (!method.available) return;
		this.selectedMethod = method.id;
	}

	confirmPayment(): void {
		// A second click while the order is being created would create a second order
		if (!this.selectedMethod || this.isCreatingOrder) return;

		if (this.selectedMethod === 'card') {
			this.paymentMethodSelected.emit(this.selectedMethod);
			this.close();
		} else if (isCryptoPaymentMethod(this.selectedMethod)) {
			// Crypto payment: emit event for parent to create order
			this.cryptoPaymentSelected.emit({ method: this.selectedMethod });
		} else {
			// For Revolut and Bizum, emit the event that creates the order and shows the instructions
			this.manualPaymentSelected.emit({ method: this.selectedMethod });
			this.showManualPaymentInstructions = true;
		}
	}

	get isCryptoMethod(): boolean {
		return !!this.selectedMethod?.startsWith('crypto_');
	}

	openRevolutApp(): void {
		const deepLink = `revolut://pay?recipient=@${this.revolutId}&amount=${this.totalAmount.toFixed(2)}&currency=EUR`;
		window.location.href = deepLink;

		// Fallback: if the app does not open, open the Revolut web with the amount parameters
		// Note: revolut.me may ignore these parameters on the web, but they are sent just in case
		setTimeout(() => {
			window.open(
				`https://revolut.me/${this.revolutId}?amount=${this.totalAmount.toFixed(2)}&currency=EUR`,
				'_blank',
			);
		}, 2000);
	}

	confirmManualPayment(): void {
		if (this.orderId) {
			this.manualPaymentConfirmed.emit({ orderId: this.orderId });
			this.close();
		}
	}

	backToMethods(): void {
		this.showManualPaymentInstructions = false;
	}

	close(): void {
		this.visible = false;
		this.visibleChange.emit(false);
		this.selectedMethod = null;
		this.showManualPaymentInstructions = false;
		this.showCryptoPaymentView = false;
		this.clearCountdown();
		this.clearStatusPolling();
		// orderId is not reset here: the parent owns it
	}

	reset(): void {
		this.selectedMethod = null;
		this.showManualPaymentInstructions = false;
		this.showCryptoPaymentView = false;
		this.cryptoPaymentDetected = false;
		this.clearCountdown();
		this.clearStatusPolling();
	}

	cancel(): void {
		// The X lives in the p-dialog `header` template, which PrimeNG mounts outside the
		// zone: without `run` visible changed but nothing re-rendered and the modal did not close.
		this.ngZone.run(() => {
			this.cancelled.emit();
			this.close();
		});
	}

	get manualPaymentInfo(): ManualPaymentInfo {
		return {
			revolutId: this.revolutId,
			revolutDeepLink: `revolut://pay?recipient=@${this.revolutId}&amount=${this.totalAmount.toFixed(2)}&currency=EUR`,
			bizumPhone: this.bizumPhone,
			amount: this.totalAmount,
		};
	}

	get revolutWebLink(): string {
		return `https://revolut.me/${this.revolutId}?amount=${this.totalAmount.toFixed(2)}&currency=EUR`;
	}

	// --- Crypto Payment Methods ---

	showCryptoInstructions(response: CreateCryptoOrderResponse): void {
		this.cryptoWalletAddress = response.walletAddress;
		this.cryptoAmount = response.cryptoAmount;
		this.cryptoCurrency = response.cryptoCurrency;
		this.cryptoCurrencyLabel = this.getCryptoCurrencyLabel(response.cryptoCurrency);
		this.cryptoExpiresAt = new Date(response.expiresAt);
		this.cryptoOrderStatus = 'PENDING_PAYMENT';
		this.cryptoPaymentDetected = false;
		this.showCryptoPaymentView = true;
		this.orderId = response.orderId;

		this.generateCryptoQr();
		this.startCountdown();
		this.startStatusPolling();
	}

	private getCryptoCurrencyLabel(currency: string): string {
		const labels: Record<string, string> = {
			BTC: 'Bitcoin (BTC)',
			USDT_TRC20: 'USDT (TRC20)',
			USDT_ERC20: 'USDT (ERC20)',
		};
		return labels[currency] || currency;
	}

	private async generateCryptoQr(): Promise<void> {
		try {
			// BTC uses bitcoin: URI scheme, USDT just uses the address
			const qrContent =
				this.cryptoCurrency === 'BTC'
					? `bitcoin:${this.cryptoWalletAddress}?amount=${this.cryptoAmount}`
					: this.cryptoWalletAddress;

			this.cryptoQrCode = await QRCode.toDataURL(qrContent, {
				width: 200,
				margin: 2,
				color: { dark: '#000000', light: '#ffffff' },
			});
		} catch (err) {
			console.error('Error generating crypto QR:', err);
		}
	}

	private startCountdown(): void {
		this.clearCountdown();
		this.updateCountdown();
		this.countdownInterval = setInterval(() => this.updateCountdown(), 1000);
	}

	private updateCountdown(): void {
		if (!this.cryptoExpiresAt) return;
		const remaining = this.cryptoExpiresAt.getTime() - Date.now();
		if (remaining <= 0) {
			this.cryptoCountdownMinutes = 0;
			this.cryptoCountdownSeconds = 0;
			this.clearCountdown();
			this.clearStatusPolling();
			this.cryptoOrderStatus = 'EXPIRED';
			return;
		}
		this.cryptoCountdownMinutes = Math.floor(remaining / 60000);
		this.cryptoCountdownSeconds = Math.floor((remaining % 60000) / 1000);
	}

	private startStatusPolling(): void {
		this.clearStatusPolling();
		this.statusPollInterval = setInterval(() => {
			if (!this.orderId) return;
			this.statusPollSub?.unsubscribe();
			this.statusPollSub = this.paymentsService.checkOrderStatus(String(this.orderId)).subscribe({
				next: (res) => {
					this.cryptoOrderStatus = res.paymentStatus;
					if (res.paymentStatus === 'COMPLETED') {
						this.cryptoPaymentDetected = true;
						this.clearCountdown();
						this.clearStatusPolling();
					}
				},
				error: (err) => console.error('Status poll error:', err),
			});
		}, 10_000); // Every 10 seconds
	}

	copyCryptoAmount(): void {
		navigator.clipboard.writeText(this.cryptoAmount).then(() => {
			this.cryptoAmountCopied = true;
			setTimeout(() => {
				this.cryptoAmountCopied = false;
			}, 2000);
		});
	}

	copyCryptoAddress(): void {
		navigator.clipboard.writeText(this.cryptoWalletAddress).then(() => {
			this.cryptoAddressCopied = true;
			setTimeout(() => {
				this.cryptoAddressCopied = false;
			}, 2000);
		});
	}

	private clearCountdown(): void {
		if (this.countdownInterval) {
			clearInterval(this.countdownInterval);
			this.countdownInterval = null;
		}
	}

	private clearStatusPolling(): void {
		if (this.statusPollInterval) {
			clearInterval(this.statusPollInterval);
			this.statusPollInterval = null;
		}
		this.statusPollSub?.unsubscribe();
		this.statusPollSub = null;
	}

	ngOnDestroy(): void {
		this.clearCountdown();
		this.clearStatusPolling();
	}
}
